import { useCallback, useState } from 'react'
import { ENV } from '../services/constant'

/**
 *
 * @param {String} url
 * @param {String} method
 * @param {Object} body
 * @param {Boolean} trigger
 * @returns  {Object} data, loading, error
 */

const useFetch = (url, method, body = null) => {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    /* FETCH Functions
    ---------------------------------------------------------------------------------------------------- */
    const makeRequest = useCallback(
        async ({ url, method, body }) => {
            setLoading(true)
            setError(null)
            setData(null)

            // Variables
            const fetchurl = url
            const fetchMethod = method
            const fetchBody = body

            // URL
            let completeUrl = ENV.api_wp.url + fetchurl

            // Options du fetch
            const options = {
                method: fetchMethod,
                mode: 'same-origin',
                cache: 'no-cache',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json, */*;q=0.1',
                    'X-WP-Nonce': ENV.api_wp.nonce || '',
                },
                redirect: 'follow',
                referrerPolicy: 'no-referrer',
            }

            // Body for POST method
            if (fetchBody) {
                options.headers['Content-Type'] = 'application/json'
                options.body = JSON.stringify(fetchBody)
            }

            // Log Fetch in debug mode
            if (ENV.wp.debug)
                console.log('Fetch WP API - request : ', {
                    url: completeUrl,
                    path: fetchurl,
                    method: fetchMethod,
                    options: options,
                })
            let response
            try {
                // Fetch
                response = await fetch(completeUrl, options)
                if (ENV.wp.debug) console.log('Fetch WP API - response :', response)

                // Save data
                const data = await response.json()

                setData(data)
            } catch (error) {
                // We catch error
                if (ENV.wp.debug) console.log('Fetch WP API - error : ', response)
                setError(error)
            } finally {
                // We stop loading
                if (ENV.wp.debug) console.log('Fetch WP API - finally ')
                setLoading(false)
            }
        },
        [url, method, body]
    )

    /* Fetchs functions
    ---------------------------------------------------------------------------------------------------- */

    // Options
    const optionSave = useCallback((option_value) => {
        makeRequest({
            url: `${ENV?.api_wp?.routes?.options}`,
            method: 'POST',
            body: option_value,
        })
    }, [])

    const optionDeleteCity = useCallback(() => {
        makeRequest({
            url: `${ENV?.api_wp?.routes?.options}baseo_comarquage_global_city`,
            method: 'DELETE',
        })
    }, [])

    // Selects
    const documentsSelect = useCallback((hearing, search) => {
        if (!search?.length > 0) return
        makeRequest({
            url: `${ENV.api_wp.routes.select_documents}?hearing=${hearing}&s=${search}`,
            method: 'GET',
        })
    }, [])

    const citiesSelect = useCallback((searchValue) => {
        if (!searchValue?.length > 0) return

        makeRequest({
            url: `${ENV.api_wp.routes.select_cities}${searchValue}`,
            method: 'GET',
        })
    }, [])

    // Get
    const servicesGet = useCallback((body) => {
        makeRequest({
            url: `${ENV.api_wp.routes.services}`,
            method: 'POST',
            body: body,
        })
    }, [])

    const documentsContentGet = useCallback((hearing, fiche) => {
        makeRequest({
            url: `${ENV.api_wp.routes.content_documents}${hearing}/${fiche}`,
            method: 'GET',
        })
    }, [])

    const searchGet = useCallback((hearing, value) => {
        if (!value?.length > 0) return
        makeRequest({
            url: `${ENV.api_wp.routes.search}${hearing}/${value}`,
            method: 'GET',
        })
    }, [])

    const refreshLicense = useCallback(() => {
        makeRequest({
            url: `${ENV.api_wp.routes.refresh_license}`,
            method: 'GET',
        })
    }, [])

    const api = {
        documentsContentGet,
        searchGet,
        servicesGet,
        optionSave,
        documentsSelect,
        citiesSelect,
        optionDeleteCity,
        refreshLicense,
    }

    /* Return Hook informations
    ---------------------------------------------------------------------------------------------------- */
    return { data, loading, error, api }
}

export default useFetch
